import { Button, Card, CardContent, Input, Label, Loader } from "@devhop/ui";
import { useEffect, useMemo, useState } from "react";
import type {
  PosSalesByCurrencyReportQueryDTO,
  PosSalesExtendedReportQueryDTO,
} from "@/modules/merchant/domain/contracts";
import { canUseMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { EMPTY_POS_SALES_CURRENCY_QUERY } from "@/modules/merchant/presentation/lib/pos-sales-currency-report-query";
import { useMerchantShopsStore } from "@/modules/merchant/presentation/store/merchant-shops-store";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import {
  formatPrice,
  isShopCurrency,
  SHOP_CURRENCIES,
} from "@/shared/lib/currency";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  usePosSalesByCurrencyReportQuery,
  usePosSalesByDayReportQuery,
  usePosTopSkusReportQuery,
  useShopBranchesListQuery,
} from "../api/queries";

/** ສະຫຼຸບຍອດຂາຍ POS ຕາມສະກຸນ — ໃຊ້ຊ້ຳໃນແດຊບອດ ແລະ ໜ້າລາຍງານ */
export function PosSalesByCurrencyReportSection({
  shopId,
}: {
  shopId: string | undefined;
}) {
  const { currentShop } = useMerchantShops(shopId);
  const shops = useMerchantShopsStore((s) => s.shops);

  const membershipsForShop = useMemo(
    () => (shopId ? shops.filter((x) => x.shopId === shopId) : []),
    [shops, shopId],
  );

  const hasShopWideBranchAccess = useMemo(
    () => membershipsForShop.some((m) => m.branchId == null),
    [membershipsForShop],
  );

  const accessibleScopedBranches = useMemo(() => {
    const m = new Map<string, string>();
    for (const row of membershipsForShop) {
      if (row.branchId) {
        m.set(
          row.branchId,
          row.branchName?.trim() || `${row.branchId.slice(0, 8)}…`,
        );
      }
    }
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [membershipsForShop]);

  const canViewSalesReport =
    shopId != null &&
    canUseMerchantPermission(
      currentShop?.permissions ?? [],
      MerchantPermissions.pos.read,
      currentShop?.verifiedLevel ?? null,
    );

  const canListBranchesForReport =
    shopId != null &&
    canUseMerchantPermission(
      currentShop?.permissions ?? [],
      MerchantPermissions.shop_staff.read,
      currentShop?.verifiedLevel ?? null,
    );

  const [reportFrom, setReportFrom] = useState("");
  const [reportTo, setReportTo] = useState("");
  const [reportBranchId, setReportBranchId] = useState<string>("__all__");
  const [topSkuLimit, setTopSkuLimit] = useState(20);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset ສາຂາເມື່ອປ່ຽນຮ້ານ
  useEffect(() => {
    setReportBranchId("__all__");
  }, [shopId]);

  const branchesListQuery = useShopBranchesListQuery(
    canViewSalesReport && hasShopWideBranchAccess && canListBranchesForReport
      ? shopId
      : undefined,
  );

  const reportQuery = useMemo((): Partial<PosSalesByCurrencyReportQueryDTO> => {
    const q: Partial<PosSalesByCurrencyReportQueryDTO> = {};
    if (reportFrom.trim()) q.createdFrom = reportFrom.trim();
    if (reportTo.trim()) q.createdTo = reportTo.trim();

    if (hasShopWideBranchAccess) {
      if (
        canListBranchesForReport &&
        reportBranchId !== "__all__" &&
        reportBranchId.trim() !== ""
      ) {
        q.branchId = reportBranchId.trim();
      }
    } else if (accessibleScopedBranches.length > 1) {
      if (reportBranchId !== "__all__" && reportBranchId.trim() !== "") {
        q.branchId = reportBranchId.trim();
      }
    }
    if (Object.keys(q).length === 0) {
      return EMPTY_POS_SALES_CURRENCY_QUERY;
    }
    return q;
  }, [
    reportFrom,
    reportTo,
    reportBranchId,
    hasShopWideBranchAccess,
    canListBranchesForReport,
    accessibleScopedBranches.length,
  ]);

  const extendedReportQuery = useMemo((): Partial<PosSalesExtendedReportQueryDTO> => {
    return { ...reportQuery, limit: topSkuLimit };
  }, [reportQuery, topSkuLimit]);

  const branchReportSelectOptions = useMemo(() => {
    if (hasShopWideBranchAccess && canListBranchesForReport) {
      const items = branchesListQuery.data ?? [];
      return [
        { value: "__all__", label: "ທຸກສາຂາ" },
        ...items.map((b) => ({
          value: b.id,
          label:
            (b.name.trim() || `${b.id.slice(0, 8)}…`) +
            (b.isActive ? "" : " · ປິດ"),
        })),
      ];
    }
    if (!hasShopWideBranchAccess && accessibleScopedBranches.length > 1) {
      return [
        { value: "__all__", label: "ທຸກສາຂາທີ່ມີສິດ" },
        ...accessibleScopedBranches.map((b) => ({
          value: b.id,
          label: b.name,
        })),
      ];
    }
    return [];
  }, [
    hasShopWideBranchAccess,
    canListBranchesForReport,
    branchesListQuery.data,
    accessibleScopedBranches,
  ]);

  useEffect(() => {
    if (reportBranchId === "__all__") return;
    const opts = branchReportSelectOptions;
    if (opts.length === 0) return;
    if (!opts.some((o) => o.value === reportBranchId)) {
      setReportBranchId("__all__");
    }
  }, [branchReportSelectOptions, reportBranchId]);

  const reportBranchScopeLabel = useMemo(() => {
    if (reportBranchId === "__all__") {
      if (hasShopWideBranchAccess) return "ທຸກສາຂາ";
      if (accessibleScopedBranches.length <= 1) {
        return accessibleScopedBranches[0]?.name ?? "ສາຂາ";
      }
      return `ສາຂາທີ່ມີສິດ (${accessibleScopedBranches.length})`;
    }
    if (hasShopWideBranchAccess) {
      return (
        branchesListQuery.data
          ?.find((b) => b.id === reportBranchId)
          ?.name?.trim() || "ສາຂາທີ່ເລືອກ"
      );
    }
    return (
      accessibleScopedBranches.find((b) => b.id === reportBranchId)?.name ??
      "ສາຂາທີ່ເລືອກ"
    );
  }, [
    reportBranchId,
    hasShopWideBranchAccess,
    accessibleScopedBranches,
    branchesListQuery.data,
  ]);

  const salesByCurrencyReport = usePosSalesByCurrencyReportQuery(
    shopId,
    reportQuery,
    canViewSalesReport,
  );
  const salesByDayReport = usePosSalesByDayReportQuery(
    shopId,
    extendedReportQuery,
    canViewSalesReport,
  );
  const topSkusReport = usePosTopSkusReportQuery(
    shopId,
    extendedReportQuery,
    canViewSalesReport,
  );

  if (!canViewSalesReport) {
    return (
      <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
        <CardContent className="py-6">
          <p className="text-muted-foreground text-sm">
            ບໍ່ມີສິດທີ່ຈຳເປັນ (pos.read) ເພື່ອເບິ່ງລາຍງານນີ້.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
      <CardContent className="space-y-4 sm:space-y-5">
        <div className="border-b border-border/60 pb-4">
          <h2 className="font-heading font-semibold text-foreground text-base sm:text-lg">
            ສະຫຼຸບຍອດຂາຍ POS ຕາມສະກຸນ
          </h2>
          <p className="mt-1.5 text-muted-foreground text-xs sm:text-sm">
            ບໍ່ແປງ FX — ລວມຍອດຕາມສະກຸນໃນບິນ.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          {hasShopWideBranchAccess && canListBranchesForReport ? (
            <div className="min-w-0 space-y-1.5 sm:min-w-56">
              <Label>ສາຂາ</Label>
              <SimpleSelect
                value={reportBranchId}
                onValueChange={setReportBranchId}
                options={branchReportSelectOptions}
                placeholder="ເລືອກສາຂາ"
                disabled={branchesListQuery.isLoading}
                className="w-full font-medium"
              />
              {branchesListQuery.isError ? (
                <p className="text-[11px] text-destructive">
                  ໂຫຼດລາຍການສາຂາບໍ່ສຳເລັດ
                </p>
              ) : null}
            </div>
          ) : hasShopWideBranchAccess && !canListBranchesForReport ? (
            <p className="text-[11px] text-muted-foreground sm:max-w-xs sm:self-end">
              ບໍ່ມີສິດອ່ານລາຍການສາຂາ — ລາຍງານລວມທຸກສາຂາຂອງຮ້ານ.
            </p>
          ) : !hasShopWideBranchAccess && accessibleScopedBranches.length > 1 ? (
            <div className="min-w-0 space-y-1.5 sm:min-w-56">
              <Label>ສາຂາ</Label>
              <SimpleSelect
                value={reportBranchId}
                onValueChange={setReportBranchId}
                options={branchReportSelectOptions}
                placeholder="ເລືອກສາຂາ"
                className="w-full font-medium"
              />
            </div>
          ) : !hasShopWideBranchAccess && accessibleScopedBranches.length ===
            1 ? (
            <div className="min-w-0 space-y-1.5 sm:min-w-56 sm:self-end">
              <Label>ສາຂາ</Label>
              <p className="rounded-md border border-border bg-muted/30 px-3 py-2 font-medium text-sm">
                {accessibleScopedBranches[0]?.name ?? "—"}
              </p>
            </div>
          ) : !hasShopWideBranchAccess && accessibleScopedBranches.length ===
            0 ? (
            <p className="text-[11px] text-destructive sm:self-end">
              ບໍ່ມີສາຂາໃນສິດ — ລາຍງານອາດບໍ່ສະແດງ
            </p>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="pos-report-from">ວັນທີເລີ່ມ</Label>
            <Input
              id="pos-report-from"
              type="date"
              value={reportFrom}
              onChange={(e) => setReportFrom(e.target.value)}
              className="w-full font-mono sm:w-44"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pos-report-to">ວັນທີສິ້ນສຸດ</Label>
            <Input
              id="pos-report-to"
              type="date"
              value={reportTo}
              onChange={(e) => setReportTo(e.target.value)}
              className="w-full font-mono sm:w-44"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pos-report-top-n">Top ສິນຄ້າ (N)</Label>
            <Input
              id="pos-report-top-n"
              type="number"
              min={1}
              max={50}
              value={topSkuLimit}
              onChange={(e) => {
                const n = Number.parseInt(e.target.value, 10);
                if (Number.isFinite(n)) {
                  setTopSkuLimit(Math.min(50, Math.max(1, n)));
                }
              }}
              className="w-full font-mono sm:w-20"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9"
            onClick={() => {
              setReportFrom("");
              setReportTo("");
              setReportBranchId("__all__");
              setTopSkuLimit(20);
            }}
          >
            ລ້າງຊ່ວງ
          </Button>
        </div>
        {salesByCurrencyReport.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        ) : salesByCurrencyReport.error ? (
          <p className="text-destructive text-sm">ໂຫຼດລາຍງານບໍ່ສຳເລັດ</p>
        ) : salesByCurrencyReport.data ? (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {SHOP_CURRENCIES.map((c) => {
                const bucket = salesByCurrencyReport.data?.byCurrency[c] ?? {
                  total: 0,
                  saleCount: 0,
                };
                return (
                  <div
                    key={c}
                    className="rounded-xl border border-border bg-card/80 px-4 py-4 shadow-sm"
                  >
                    <p className="font-heading font-semibold text-muted-foreground text-xs uppercase tracking-wide">
                      {c}
                    </p>
                    <p className="mt-2 font-bold font-heading text-foreground text-xl tabular-nums tracking-tight">
                      {formatPrice(bucket.total, c)}
                    </p>
                    <p className="mt-1 text-muted-foreground text-xs">
                      {bucket.saleCount} ບິນ
                    </p>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              ລວມ{" "}
              <span className="font-medium tabular-nums">
                {salesByCurrencyReport.data.includedSaleCount}
              </span>{" "}
              ບິນໃນຊ່ວງທີ່ເລືອກ (
              <span className="font-medium">{reportBranchScopeLabel}</span>).
              ວ່າງວັນທີ = ທຸກຊ່ວງເວລາ.
            </p>

            <div className="border-border border-t pt-4">
              <h3 className="font-heading font-semibold text-foreground text-sm">
                ຍອດຕໍ່ວັນ
              </h3>
              {salesByDayReport.isLoading ? (
                <div className="flex justify-center py-6">
                  <Loader />
                </div>
              ) : salesByDayReport.error ? (
                <p className="text-destructive text-sm">ໂຫຼດລາຍງານຮາຍວັນບໍ່ສຳເລັດ</p>
              ) : salesByDayReport.data && salesByDayReport.data.days.length > 0 ? (
                <div className="mt-2 max-h-72 overflow-auto rounded-md border border-border">
                  <table className="w-full min-w-[32rem] text-left text-sm">
                    <thead className="sticky top-0 border-border border-b bg-muted/80">
                      <tr>
                        <th className="px-2 py-1.5 font-medium">ວັນທີ</th>
                        {SHOP_CURRENCIES.map((c) => (
                          <th key={c} className="px-2 py-1.5 font-medium tabular-nums">
                            {c}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {salesByDayReport.data.days.map((d) => (
                        <tr
                          key={d.date}
                          className="border-border border-b border-dashed"
                        >
                          <td className="px-2 py-1.5 font-mono text-xs">{d.date}</td>
                          {SHOP_CURRENCIES.map((c) => (
                            <td key={c} className="px-2 py-1.5 tabular-nums text-xs">
                              {formatPrice(d.byCurrency[c]?.total ?? 0, c)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="mt-1 text-muted-foreground text-xs">
                  ບໍ່ມີລາຍການໃນຊ່ວງນີ້
                </p>
              )}
            </div>

            <div className="border-border border-t pt-4">
              <h3 className="font-heading font-semibold text-foreground text-sm">
                ສິນຄ້າຂາຍດີ (ຕາມຍອດລາຍການ)
              </h3>
              {topSkusReport.isLoading ? (
                <div className="flex justify-center py-6">
                  <Loader />
                </div>
              ) : topSkusReport.error ? (
                <p className="text-destructive text-sm">ໂຫຼດ Top SKU ບໍ່ສຳເລັດ</p>
              ) : topSkusReport.data && topSkusReport.data.items.length > 0 ? (
                <div className="mt-2 max-h-72 overflow-auto rounded-md border border-border">
                  <table className="w-full min-w-[28rem] text-left text-sm">
                    <thead className="sticky top-0 border-border border-b bg-muted/80">
                      <tr>
                        <th className="px-2 py-1.5 font-medium">#</th>
                        <th className="px-2 py-1.5 font-medium">ສິນຄ້າ</th>
                        <th className="px-2 py-1.5 font-medium">SKU</th>
                        <th className="px-2 py-1.5 font-medium tabular-nums">ຈຳນວນ</th>
                        <th className="px-2 py-1.5 font-medium">ສະກຸນ</th>
                        <th className="px-2 py-1.5 font-medium tabular-nums">ຍອດ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topSkusReport.data.items.map((row, i) => (
                        <tr
                          key={row.shopProductSkuId}
                          className="border-border border-b border-dashed"
                        >
                          <td className="px-2 py-1.5 text-muted-foreground text-xs">
                            {i + 1}
                          </td>
                          <td className="px-2 py-1.5 text-xs leading-snug">
                            {row.productName}
                          </td>
                          <td className="px-2 py-1.5 font-mono text-xs">
                            {row.shopSku}
                          </td>
                          <td className="px-2 py-1.5 tabular-nums text-xs">
                            {row.qty}
                          </td>
                          <td className="px-2 py-1.5 text-xs">
                            {String(row.currency)}
                          </td>
                          <td className="px-2 py-1.5 tabular-nums text-xs">
                            {(() => {
                              const cur = String(row.currency);
                              return isShopCurrency(cur)
                                ? formatPrice(row.lineSum, cur)
                                : row.lineSum.toFixed(2);
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="mt-1 text-muted-foreground text-xs">
                  ບໍ່ມີລາຍການໃນຊ່ວງນີ້
                </p>
              )}
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
